/**
 * Agency Image Library API client.
 *
 * Built on the shared apiClient (lib/apiClient), which attaches the Supabase auth
 * token, transparently refreshes it on 401, and passes FormData through untouched
 * (no forced Content-Type) — so multipart upload works without special-casing.
 */

import apiClient from './apiClient';

const base = (agencyId) => `/api/agencies/${agencyId}/images`;

const agencyImagesApi = {
  // Library list for one agency (optionally filtered by image type).
  list: (agencyId, type) =>
    apiClient.get(base(agencyId), type ? { params: { type } } : undefined),

  // Manual "Fetch logos" trigger — scrape + store candidates for one agency.
  fetchLogos: (agencyId) => apiClient.post(`${base(agencyId)}/fetch`, {}),

  // Batch fetch for agencies missing an active logo (admin → all; else owned).
  fetchAll: (concurrency = 3) =>
    apiClient.post('/api/agencies/images/fetch-all', { concurrency }),

  setDefault: (agencyId, imageId) =>
    apiClient.request(`${base(agencyId)}/${imageId}`, { method: 'PATCH', body: { is_default: true } }),

  setStatus: (agencyId, imageId, status) =>
    apiClient.request(`${base(agencyId)}/${imageId}`, { method: 'PATCH', body: { status } }),

  remove: (agencyId, imageId) => apiClient.del(`${base(agencyId)}/${imageId}`),

  // Re-crop an image's thumbnail from a manual rect ({x,y,w,h} fractions 0-1),
  // or pass null to reset to the full-image auto-crop.
  crop: (agencyId, imageId, rect) =>
    apiClient.post(`${base(agencyId)}/${imageId}/crop`, { crop: rect }),

  // Multipart upload of a user-supplied image. FormData → apiClient sends it raw
  // (no Content-Type) while still attaching the auth token.
  upload: (agencyId, file) => {
    const formData = new FormData();
    formData.append('image', file);
    return apiClient.post(base(agencyId), formData);
  },

  // Bulk default-logo URLs for many agencies at once (used by the card grid).
  defaults: (ids, type = 'logo') =>
    apiClient.get('/api/agencies/images/defaults', { params: { ids: ids.join(','), type } }),

  // ── Department Branding Library ──
  // Cross-department browse: one row per active department + its default mark's kit.
  brandLibrary: ({ q = '', filter = 'all', limit = 60, offset = 0 } = {}) =>
    apiClient.get('/api/agencies/brand/library', { params: { q, filter, limit, offset } }),

  // One department: agency facts + every non-rejected mark with its kit.
  brand: (agencyId) => apiClient.get(`/api/agencies/${agencyId}/brand`),

  // Build/rebuild the deck-ready kit (transparent, white, black, palette) for one mark.
  buildKit: (agencyId, imageId) => apiClient.post(`${base(agencyId)}/${imageId}/kit`, { force: true }),

  // Admin: kit every default that lacks one (bounded per call).
  buildMissingKits: (limit = 25) => apiClient.post('/api/agencies/brand/build-missing', { limit }),
};

export default agencyImagesApi;
